import type { AxiosInstance } from "axios";
import { isValidId } from "../u2000Core/tools/U2000IdUtils";
import { U2000ApiValueToDisplayYmd } from "../u2000Core/tools/U2000DateTimeHelpers";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3101";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  HOUR_BANK_HISTORY: (id: string | number) => string;
  HOUR_BANK_CLONE_NEXT: (id: string | number) => string;
  HOUR_BANK_CLONE_NEXT_BULK: string;
  HOUR_BANK_YEAR_UPDATE: (id: string | number, year: number) => string;
  SALARY_HISTORY: (id: string | number) => string;
  SALARY_HISTORY_ENTRY: (id: string | number, entryId: string) => string;
  FINDCOUNTRIES: string;
  FINDSTATES: string;
  FINDAFFILIATION: string;
  FINDEMPLOYEE: string;
  EXPORT_EXCEL: string;
};

/** Valeurs API exactes (casse respectée). */
export type U3101RemunerationType = "Salary" | "Hourly";

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  HOUR_BANK_HISTORY: (id: string | number) => `${LOGICALUNIT_URL}/${id}/hour-bank-history`,
  HOUR_BANK_CLONE_NEXT: (id: string | number) => `${LOGICALUNIT_URL}/${id}/hour-bank-years/clone-next`,
  HOUR_BANK_CLONE_NEXT_BULK: `${LOGICALUNIT_URL}/hour-bank-years/clone-next-bulk`,
  HOUR_BANK_YEAR_UPDATE: (id: string | number, year: number) => `${LOGICALUNIT_URL}/${id}/hour-bank-years/${year}`,
  SALARY_HISTORY: (id: string | number) => `${LOGICALUNIT_URL}/${id}/salary-history`,
  SALARY_HISTORY_ENTRY: (id: string | number, entryId: string) =>
    `${LOGICALUNIT_URL}/${id}/salary-history/${entryId}`,
  FINDCOUNTRIES: `${LOGICALUNIT_URL}/findCountries`,
  FINDSTATES: `${LOGICALUNIT_URL}/findStates`,
  FINDAFFILIATION: `${LOGICALUNIT_URL}/findAffiliation`,
  FINDEMPLOYEE: `${LOGICALUNIT_URL}/findEmployee`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
};

export interface U3101Employee {
  id: string;
  codeEmployee: string;
  numEmployee: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  phoneNumber?: string;
  cellPhone?: string;
  otherPhone?: string;
  otherPhoneExt?: string;
  otherPhoneComment?: string;
  email?: string;
  hourRate: number;
  /** Obligatoire à la création / mise à jour / lecture. */
  remunerationType: U3101RemunerationType;
  /** Base horaire (optionnelle ; pertinente surtout si Hourly). */
  hourlyBase: number | null;
  vacationBncHours: number;
  /** Quota / valeur initiale vacances année civile en cours (BL-0315). */
  vacationBncHoursInitial: number;
  /** Seuil toléré de vacances négatives (heures), configuré par employé. */
  vacationNegativeThresholdHours: number;
  holidayBncHours: number;
  holidayBncHoursInitial: number;
  sickLeaveBncHours: number;
  sickLeaveBncHoursInitial: number;
  specialBncHours: number;
  specialBncHoursInitial: number;
  /** Date de naissance (jour civil `yyyy-MM-dd`). Null = pas d’événement calendrier. */
  birthDate: string | null;
  /** Opt-out : ne pas afficher l’anniversaire dans le calendrier U3301. Défaut métier : false. */
  hideBirthdayInCalendar: boolean;
  isActive: boolean;
  operationType?: string[];
  affiliation?: {
    id: string;
    /** Prénom + nom, affiché après sélection. */
    name?: string;
    username?: string;
    organizationUser?: string;
  } | null;
  country: {
    id: string;
    name: string;
    countryName?: string;
    countryNameFr?: string;
    countryNameEn?: string;
    countryCode?: string;
  };
  state: {
    id: string;
    name: string;
    stateName?: string;
    stateNameFr?: string;
    stateNameEn?: string;
    stateCode?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface U3101Country {
  id: string;
  countryCode?: string;
  countryName?: string;
  countryNameEn?: string;
  countryNameFr?: string;
}

export interface U3101State {
  id: string;
  stateName?: string;
  stateNameEn?: string;
  stateNameFr?: string;
}

export const emptyEmployee: U3101Employee = {
  id: "",
  codeEmployee: "",
  numEmployee: "",
  firstName: "",
  lastName: "",
  addressLine1: "",
  city: "",
  postalCode: "",
  phoneNumber: "",
  cellPhone: "",
  otherPhone: "",
  otherPhoneExt: "",
  otherPhoneComment: "",
  email: "",
  hourRate: 0,
  remunerationType: "Hourly",
  hourlyBase: 37.5,
  vacationBncHours: 0,
  vacationBncHoursInitial: 0,
  vacationNegativeThresholdHours: 0,
  holidayBncHours: 0,
  holidayBncHoursInitial: 0,
  sickLeaveBncHours: 0,
  sickLeaveBncHoursInitial: 0,
  specialBncHours: 0,
  specialBncHoursInitial: 0,
  birthDate: null,
  hideBirthdayInCalendar: false,
  isActive: true,
  operationType: [],
  affiliation: null,
  country: {
    id: "",
    name: "",
  },
  state: {
    id: "",
    name: "",
  },
  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: null,
};

/**
 * Valeurs par défaut côté UX si l’API ne renvoie pas encore le type (données héritées).
 */
export function normalizeU3101EmployeeFromApi(employee: U3101Employee): U3101Employee {
  const rt = employee.remunerationType;
  const remunerationType: U3101RemunerationType =
    rt === "Salary" || rt === "Hourly" ? rt : "Hourly";
  const hb = employee.hourlyBase;
  const birthDate = U2000ApiValueToDisplayYmd(employee.birthDate);
  const toHours = (v: number | undefined | null) =>
    v === undefined || v === null || Number.isNaN(Number(v)) ? 0 : Number(v);
  return {
    ...employee,
    remunerationType,
    hourlyBase: hb === undefined || hb === null ? null : Number(hb),
    vacationBncHours: toHours(employee.vacationBncHours),
    vacationBncHoursInitial: toHours(employee.vacationBncHoursInitial),
    holidayBncHours: toHours(employee.holidayBncHours),
    holidayBncHoursInitial: toHours(employee.holidayBncHoursInitial),
    sickLeaveBncHours: toHours(employee.sickLeaveBncHours),
    sickLeaveBncHoursInitial: toHours(employee.sickLeaveBncHoursInitial),
    specialBncHours: toHours(employee.specialBncHours),
    specialBncHoursInitial: toHours(employee.specialBncHoursInitial),
    birthDate,
    hideBirthdayInCalendar: employee.hideBirthdayInCalendar === true,
  };
}

type U3101EmployeeCreatePayload = U3101Employee;
type U3101EmployeeUpdatePayload = Omit<U3101Employee, "codeEmployee">;

function sanitizeEmployeePayload<T extends Record<string, unknown>>(payload: T): T {
  const cleaned = { ...payload } as T & {
    undefined?: unknown;
    birthDate?: string | null;
    hideBirthdayInCalendar?: boolean;
  };
  // Protection contre un bind de champ invalide qui injecte la clé littérale "undefined"
  delete cleaned.undefined;
  const birth = cleaned.birthDate;
  if (birth === undefined || birth === null || birth === "") {
    cleaned.birthDate = null;
  } else {
    cleaned.birthDate = String(birth).slice(0, 10);
  }
  cleaned.hideBirthdayInCalendar = cleaned.hideBirthdayInCalendar === true;
  return cleaned as T;
}

function buildEmployeeCreatePayload(data: U3101Employee): U3101EmployeeCreatePayload {
  const payload = sanitizeEmployeePayload({ ...data });
  if (!payload.affiliation || !isValidId(payload.affiliation.id)) {
    payload.affiliation = null;
  }
  return payload;
}

export function buildEmployeeUpdatePayload(data: U3101Employee): U3101EmployeeUpdatePayload {
  const payload = sanitizeEmployeePayload({ ...data }) as U3101Employee & {
    undefined?: unknown;
    codeEmployee?: string;
  };
  if (!payload.affiliation || !isValidId(payload.affiliation.id)) {
    payload.affiliation = null;
  }
  delete (payload as { codeEmployee?: string }).codeEmployee;
  return payload;
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, data: U3101Employee) => {
  return axios.post(LogicalUnitEndpoints.CREATE, buildEmployeeCreatePayload(data));
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U3101Employee) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), buildEmployeeUpdatePayload(data));
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U3101Employee) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), data);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

/** Types de banque d’heures BNC (wire API / historique — BL-0315). */
export type U3101HourBankType = "vacation" | "holidayLeave" | "sickLeave" | "special";

export const U3101_HOUR_BANK_TYPES: readonly U3101HourBankType[] = [
  "vacation",
  "holidayLeave",
  "sickLeave",
  "special",
] as const;

export type U3101HourBankYearStatus = "open" | "prepared" | "closed";

export interface U3101HourBankYearBank {
  bankType: U3101HourBankType;
  initialHours: number;
  closingBalanceHours: number | null;
  consumedHours: number | null;
}

export interface U3101HourBankYear {
  year: number;
  status: U3101HourBankYearStatus;
  banks: U3101HourBankYearBank[];
}

export interface U3101HourBankHistory {
  years: U3101HourBankYear[];
}

export interface U3101HourBankYearUpdateBank {
  bankType: U3101HourBankType;
  initialHours: number;
}

export interface U3101HourBankYearUpdatePayload {
  banks: U3101HourBankYearUpdateBank[];
}

export type U3101HourBankBulkCloneMode = "fromPrevious" | "custom";

export interface U3101HourBankBulkCloneBank {
  bankType: U3101HourBankType;
  initialHours: number;
  overrideEmployee: boolean;
}

export type U3101HourBankBulkClonePayload =
  | { mode: "fromPrevious" }
  | { mode: "custom"; banks: U3101HourBankBulkCloneBank[] };

export interface U3101HourBankBulkCloneError {
  employeeId: string;
  codeEmployee?: string;
  message: string;
}

export interface U3101HourBankBulkCloneResult {
  sourceYear: number;
  targetYear: number;
  mode: U3101HourBankBulkCloneMode;
  created: number;
  skippedAlreadyExists: number;
  processed: number;
  errors: U3101HourBankBulkCloneError[];
}

function parseHourBankYearStatus(status: unknown): U3101HourBankYearStatus {
  if (status === "closed") return "closed";
  if (status === "prepared") return "prepared";
  return "open";
}

export const doGetHourBankHistory = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.HOUR_BANK_HISTORY(id), { params });
};

export const doCloneHourBankYearToNext = (axios: AxiosInstance, id: string | number) => {
  return axios.post(LogicalUnitEndpoints.HOUR_BANK_CLONE_NEXT(id), {});
};

export const doCloneHourBankYearToNextBulk = (
  axios: AxiosInstance,
  payload: U3101HourBankBulkClonePayload,
) => {
  return axios.post(LogicalUnitEndpoints.HOUR_BANK_CLONE_NEXT_BULK, payload);
};

export const doUpdateHourBankYear = (
  axios: AxiosInstance,
  id: string | number,
  year: number,
  payload: U3101HourBankYearUpdatePayload,
) => {
  return axios.post(LogicalUnitEndpoints.HOUR_BANK_YEAR_UPDATE(id, year), payload);
};

export function parseU3101HourBankBulkCloneResultFromApi(data: unknown): U3101HourBankBulkCloneResult | null {
  const root = data as { result?: unknown };
  const r = (root?.result ?? data) as Record<string, unknown> | null;
  if (!r || typeof r !== "object") return null;
  const errorsRaw = Array.isArray(r.errors) ? r.errors : [];
  return {
    sourceYear: Number(r.sourceYear ?? 0),
    targetYear: Number(r.targetYear ?? 0),
    mode: r.mode === "custom" ? "custom" : "fromPrevious",
    created: Number(r.created ?? 0),
    skippedAlreadyExists: Number(r.skippedAlreadyExists ?? 0),
    processed: Number(r.processed ?? 0),
    errors: errorsRaw.map((e) => {
      const err = (e ?? {}) as Record<string, unknown>;
      return {
        employeeId: String(err.employeeId ?? ""),
        codeEmployee: err.codeEmployee != null ? String(err.codeEmployee) : undefined,
        message: String(err.message ?? ""),
      };
    }),
  };
}

export function parseU3101HourBankHistoryFromApi(data: unknown): U3101HourBankHistory | null {
  const root = data as { result?: unknown };
  const r = (root?.result ?? data) as { years?: unknown } | null;
  if (!r || typeof r !== "object" || !Array.isArray(r.years)) {
    return null;
  }
  const years: U3101HourBankYear[] = r.years.map((y) => {
    const yearRec = (y ?? {}) as Record<string, unknown>;
    const banksRaw = Array.isArray(yearRec.banks) ? yearRec.banks : [];
    const banks: U3101HourBankYearBank[] = banksRaw.map((b) => {
      const bank = (b ?? {}) as Record<string, unknown>;
      const bankType = String(bank.bankType ?? "") as U3101HourBankType;
      return {
        bankType,
        initialHours: Number(bank.initialHours ?? 0),
        closingBalanceHours:
          bank.closingBalanceHours === null || bank.closingBalanceHours === undefined
            ? null
            : Number(bank.closingBalanceHours),
        consumedHours:
          bank.consumedHours === null || bank.consumedHours === undefined
            ? null
            : Number(bank.consumedHours),
      };
    });
    return {
      year: Number(yearRec.year ?? 0),
      status: parseHourBankYearStatus(yearRec.status),
      banks,
    };
  });
  return { years };
}

/** Statuts historique salaires (BL-0442). */
export type U3101SalaryHistoryStatus = "current" | "planned" | "historical";

export type U3101SalaryHistoryApplyMode = "now" | "later";

export interface U3101SalaryHistoryEntry {
  id: string;
  version?: number;
  effectiveDate: string;
  remunerationType: U3101RemunerationType;
  hourRate: number;
  hourlyBase: number | null;
  status: U3101SalaryHistoryStatus;
  comment: string | null;
  jobId: string | null;
  createdAt?: string;
}

export interface U3101SalaryHistory {
  entries: U3101SalaryHistoryEntry[];
  /** Au moins une FT approuvée (Approve / WaitingForGenerateTEC / Close) — verrouille l’édition directe fiche. */
  hasApprovedTimesheet: boolean;
}

export interface U3101SalaryHistoryCreatePayload {
  applyMode: U3101SalaryHistoryApplyMode;
  effectiveDate: string;
  remunerationType: U3101RemunerationType;
  hourRate: number;
  hourlyBase?: number | null;
  comment?: string | null;
}

export interface U3101SalaryHistoryUpdatePayload {
  effectiveDate: string;
  remunerationType: U3101RemunerationType;
  hourRate: number;
  hourlyBase?: number | null;
  comment?: string | null;
}

function parseSalaryHistoryStatus(status: unknown): U3101SalaryHistoryStatus {
  if (status === "planned") return "planned";
  if (status === "historical") return "historical";
  return "current";
}

function parseRemunerationType(value: unknown): U3101RemunerationType {
  return value === "Salary" ? "Salary" : "Hourly";
}

export const doGetSalaryHistory = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.SALARY_HISTORY(id), { params });
};

export const doCreateSalaryHistory = (
  axios: AxiosInstance,
  id: string | number,
  payload: U3101SalaryHistoryCreatePayload,
) => {
  return axios.post(LogicalUnitEndpoints.SALARY_HISTORY(id), payload);
};

export const doUpdateSalaryHistory = (
  axios: AxiosInstance,
  id: string | number,
  entryId: string,
  payload: U3101SalaryHistoryUpdatePayload,
) => {
  return axios.patch(LogicalUnitEndpoints.SALARY_HISTORY_ENTRY(id, entryId), payload);
};

export const doDeleteSalaryHistory = (axios: AxiosInstance, id: string | number, entryId: string) => {
  return axios.delete(LogicalUnitEndpoints.SALARY_HISTORY_ENTRY(id, entryId));
};

export function parseU3101SalaryHistoryFromApi(data: unknown): U3101SalaryHistory | null {
  const root = data as { result?: unknown };
  const r = (root?.result ?? data) as { entries?: unknown; hasApprovedTimesheet?: unknown } | null;
  if (!r || typeof r !== "object" || !Array.isArray(r.entries)) {
    return null;
  }
  const entries: U3101SalaryHistoryEntry[] = r.entries.map((raw) => {
    const e = (raw ?? {}) as Record<string, unknown>;
    const effectiveRaw = e.effectiveDate;
    const effectiveDate =
      typeof effectiveRaw === "string"
        ? U2000ApiValueToDisplayYmd(effectiveRaw) || effectiveRaw.slice(0, 10)
        : "";
    return {
      id: String(e.id ?? ""),
      version: e.version === undefined || e.version === null ? undefined : Number(e.version),
      effectiveDate,
      remunerationType: parseRemunerationType(e.remunerationType),
      hourRate: Number(e.hourRate ?? 0),
      hourlyBase:
        e.hourlyBase === null || e.hourlyBase === undefined ? null : Number(e.hourlyBase),
      status: parseSalaryHistoryStatus(e.status),
      comment: e.comment === null || e.comment === undefined ? null : String(e.comment),
      jobId: e.jobId === null || e.jobId === undefined ? null : String(e.jobId),
      createdAt: e.createdAt === undefined || e.createdAt === null ? undefined : String(e.createdAt),
    };
  });
  return {
    entries,
    hasApprovedTimesheet: r.hasApprovedTimesheet === true,
  };
}

export function u3101SalaryHistoryHasCurrent(history: U3101SalaryHistory | null | undefined): boolean {
  return Boolean(history?.entries?.some((e) => e.status === "current"));
}

/** Entrée salaire en vigueur (`status === current`), si présente. */
export function u3101SalaryHistoryCurrentEntry(
  history: U3101SalaryHistory | null | undefined,
): U3101SalaryHistoryEntry | null {
  return history?.entries?.find((e) => e.status === "current") ?? null;
}

/** Panneau rémunération en lecture seule si une FT approuvée existe (directives BL-0442). */
export function u3101SalaryHistoryRemunerationLocked(
  history: U3101SalaryHistory | null | undefined,
): boolean {
  return Boolean(history?.hasApprovedTimesheet);
}

export const doFindCountries = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDCOUNTRIES, criterias);
};

export const doFindStates = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDSTATES, criterias);
};

export const doFindAffiliation = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDAFFILIATION, criterias);
};

export type U3101AutoCompletCriterias = { fieldName: string; value: unknown }[];

export interface U3101FindEmployeeItem {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
}

export const doFindEmployee = (axios: AxiosInstance, criterias: U3101AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDEMPLOYEE, criterias);
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function luHandleAppiError(
  t: TranslateFn,
  nameSpace: string,
  err: unknown,
  errorResult: U2000ApiErrorResult,
) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case "BU310101":
    case "BU310102":
    case "BU310103":
    case "BU310104":
    case "BU310105":
    case "BU310106":
    case "BU310107":
    case "BU310108":
    case "BU310109":
    case "BU310110":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace, defaultValue: "Erreur" }),
        text: t(`error${apiErr.errorCode}`, {
          ns: nameSpace,
          defaultValue: apiErr.description ?? apiErr.errorCode,
        }),
      });
      break;
    default:
      break;
  }
}
